/**
 * Module-level CSRF token cache.
 *
 * The backend issues CSRF tokens via GET /auth/csrf-token (double-submit
 * cookie pattern). The token is short-lived (per-session) and cheap to
 * fetch, but caching avoids the round-trip on every mutating request.
 *
 * Cache lifecycle:
 * - First mutating request → fetch token, cache, send.
 * - Subsequent mutating requests → reuse cached token.
 * - 403 with CSRF error → clear cache, fetch fresh, retry once.
 * - Logout / session change → caller invokes `clearCsrfTokenCache()`.
 *
 * The cache is INTENTIONALLY module-level (singleton). All consumers in
 * a portal share one token. Cross-portal contamination cannot happen
 * because each portal builds its own bundle and runs in its own browser
 * tab.
 *
 * The token is treated as a short-lived secret: never log it, never
 * persist to localStorage / IndexedDB / cookies.
 */
import { fetchCsrfToken } from '@artemis/contracts';
let cached = null;
let inflight = null;
export async function getCsrfToken(baseUrl, fetchImpl = fetch) {
    if (cached !== null) {
        return cached;
    }
    // Coalesce concurrent first-callers so we issue ONE network request even
    // if N mutations fire simultaneously on app boot.
    if (inflight !== null) {
        return inflight;
    }
    inflight = fetchCsrfToken(baseUrl, fetchImpl)
        .then((token) => {
        cached = token;
        return token;
    })
        .finally(() => {
        inflight = null;
    });
    return inflight;
}
export function clearCsrfTokenCache() {
    cached = null;
    inflight = null;
}
/** @internal — for tests that need to assert cache state. */
export function _peekCsrfCache() {
    return cached;
}
//# sourceMappingURL=csrf-cache.js.map